/**
 * Normalizes backend strings that still contain "RFI" into VQQ terminology for UI display only.
 * Responses are cloned (shallow + mapped item arrays) so React Query caches stay predictable.
 *
 * Keep server/API identifiers (e.g. document_type: "RFI") unchanged — only copy used for rendering is updated.
 */

/**
 * Replaces RFI terminology in a single display string (case-insensitive for API variance).
 * Order matters: longer / plural tokens first.
 * - Whole word RFIS / RFIs / rfis → VQQs
 * - Whole word RFI / rfi → VQQ
 *
 * Non-strings are returned as-is so callers can pass unknown shapes safely.
 *
 * @param {unknown} value
 * @returns {unknown}
 */
export function replaceRfiTerminologyForDisplay(value) {
  if (value == null) return value;
  if (typeof value !== "string") return value;
  return value
    .replace(/\bRFIS\b/gi, "VQQs")
    .replace(/\bRFIs\b/gi, "VQQs")
    .replace(/\bRFI\b/gi, "VQQ");
}

/**
 * Maps `services` from get_rfis-style rows: string, array of strings, or array of objects
 * with common label fields (matches normalization paths in myRfiGridRowMapper).
 *
 * @param {unknown} raw
 * @returns {unknown}
 */
function mapGetRfisServicesFieldForDisplay(raw) {
  if (raw == null) return raw;
  if (typeof raw === "string") {
    return replaceRfiTerminologyForDisplay(raw);
  }
  if (Array.isArray(raw)) {
    return raw.map((entry) => {
      if (typeof entry === "string") {
        return replaceRfiTerminologyForDisplay(entry);
      }
      if (entry && typeof entry === "object" && !Array.isArray(entry)) {
        const next = { ...entry };
        for (const key of ["name", "label", "services", "rfi_name"]) {
          if (typeof next[key] === "string") {
            next[key] = replaceRfiTerminologyForDisplay(next[key]);
          }
        }
        return next;
      }
      return entry;
    });
  }
  return raw;
}

/**
 * Single row from GET / RFI-Templates/get_rfis or admin/get_rfis.
 *
 * @param {object} item
 * @returns {object}
 */
export function mapGetRfisRowForRfiToVqqDisplay(item) {
  if (!item || typeof item !== "object") return item;
  const typeRaw = item.type ?? item.Type;
  const next = {
    ...item,
    name:
      typeof item.name === "string"
        ? replaceRfiTerminologyForDisplay(item.name)
        : item.name,
    services: mapGetRfisServicesFieldForDisplay(item.services),
  };
  if (typeof typeRaw === "string" && typeRaw.trim() !== "") {
    next.type = replaceRfiTerminologyForDisplay(typeRaw);
  }
  return next;
}

/**
 * Response body from `POST .../RFI-Templates/admin/get_rfis` or `.../RFI-Templates/get_rfis`.
 * Mutates only `items[]` when present.
 *
 * @param {object} [apiData]
 * @returns {object|undefined}
 */
export function applyRfiToVqqDisplayToGetRfisResponse(apiData) {
  if (!apiData || typeof apiData !== "object") return apiData;
  const next = { ...apiData };
  if (Array.isArray(next.items)) {
    next.items = next.items.map(mapGetRfisRowForRfiToVqqDisplay);
  }
  return next;
}

/** String keys on capability objects that may carry RFI labels (Library Requests + Capabilities UI). */
const LIBRARY_CAPABILITY_STRING_KEYS = [
  "rfi_name",
  "services",
  "name",
  "label",
  "service_compliance_type",
  "geographical_region",
  "indication",
  "therapeutic_area",
  "diversity_classification",
];

/**
 * First non-empty display string for a capability (matches common UI fallbacks: rfi_name → services → name).
 * Skips `""` so a blank `rfi_name` does not block reading the label from `services` (API sometimes splits fields).
 *
 * @param {object} c
 * @returns {string|undefined}
 */
function firstCapabilityDisplayString(c) {
  const candidates = [
    c.rfi_name,
    c.Rfi_Name,
    c.rfiName,
    c.services,
    c.Services,
    c.name,
    c.Name,
  ];
  for (const v of candidates) {
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return undefined;
}

/**
 * If `rfi_name` is still empty after string replacement, copy the primary label so `Capabilities` / grids see one shape.
 *
 * @param {object} c
 */
function ensureCapabilityRfiNameWhenEmpty(c) {
  const primary = firstCapabilityDisplayString(c);
  if (primary === undefined) return;
  const empty =
    c.rfi_name == null ||
    (typeof c.rfi_name === "string" && c.rfi_name.trim() === "");
  if (empty) {
    c.rfi_name = primary;
  }
}

/**
 * Maps one capability entry; keep IDs (rfi_id, etc.) unchanged.
 *
 * @param {object} cap
 * @returns {object}
 */
function mapLibraryRequestCapabilityForDisplay(cap) {
  if (!cap || typeof cap !== "object" || Array.isArray(cap)) return cap;
  const c = { ...cap };
  for (const key of LIBRARY_CAPABILITY_STRING_KEYS) {
    if (typeof c[key] === "string") {
      c[key] = replaceRfiTerminologyForDisplay(c[key]);
    }
  }
  // Alternate property names (optional .NET JSON)
  for (const altKey of ["Rfi_Name", "RfiName", "Services", "Name", "Label"]) {
    if (typeof c[altKey] === "string") {
      c[altKey] = replaceRfiTerminologyForDisplay(c[altKey]);
    }
  }

  ensureCapabilityRfiNameWhenEmpty(c);
  return c;
}

/**
 * Single Library Requests row from Documents/Requests or Documents/Admin/Requests.
 * Scope column uses `getLibraryRequestScopeCommaSeparated` → rfi_name || services || name on each capability.
 *
 * @param {object} item
 * @returns {object}
 */
export function mapDocumentsRequestRowForRfiToVqqDisplay(item) {
  if (!item || typeof item !== "object") return item;
  const next = { ...item };
  if (typeof next.document_name === "string") {
    next.document_name = replaceRfiTerminologyForDisplay(next.document_name);
  }
  if (Array.isArray(next.capabilities)) {
    next.capabilities = next.capabilities.map(mapLibraryRequestCapabilityForDisplay);
  }
  return next;
}

/** Keys wrapping paginated `items` on Document Requests API payloads. */
const DOCUMENTS_REQUEST_LIST_WRAPPERS = [
  "document_request_for_provider",
  "document_request_for_sponsor",
];

/**
 * Applies display mapping inside each known wrapper's `items` array.
 * Also maps root-level `items` — `POST Documents/Admin/Requests` returns `{ items, total_pages }`
 * without `document_request_for_*` (Library Requests admin UI reads `data.items` directly).
 * Extend `DOCUMENTS_REQUEST_LIST_WRAPPERS` if the backend adds another envelope key.
 *
 * @param {object} [apiData]
 * @returns {object|undefined}
 */
export function applyRfiToVqqDisplayToDocumentsRequestsResponse(apiData) {
  if (!apiData || typeof apiData !== "object") return apiData;
  const next = { ...apiData };
  if (Array.isArray(next.items)) {
    next.items = next.items.map(mapDocumentsRequestRowForRfiToVqqDisplay);
  }
  for (const key of DOCUMENTS_REQUEST_LIST_WRAPPERS) {
    if (next[key] && typeof next[key] === "object") {
      const container = { ...next[key] };
      if (Array.isArray(container.items)) {
        container.items = container.items.map(mapDocumentsRequestRowForRfiToVqqDisplay);
      }
      next[key] = container;
    }
  }
  return next;
}

/**
 * Generic helper for future endpoints: map `items` with a custom per-row mapper.
 * Use when a new list API returns `{ items: [...], total_pages, ... }`.
 *
 * @template T
 * @param {object} [apiData]
 * @param {(row: T) => T} mapRow - Pure function; should shallow-clone row objects.
 * @returns {object|undefined}
 */
export function applyDisplayMapToPaginatedItems(apiData, mapRow) {
  if (!apiData || typeof apiData !== "object") return apiData;
  if (typeof mapRow !== "function") return apiData;
  const next = { ...apiData };
  if (Array.isArray(next.items)) {
    next.items = next.items.map(mapRow);
  }
  return next;
}
